import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { WhatsAppIcon, InstagramIcon } from '../../../../components/icons/CustomIcons';

import { canRenderRemoteAvatarUrl, isImageFailed, markImageAsFailed } from '../../../../utils/failedImageCache';

interface ChatAvatarProps {
    name: string;
    avatar?: string;
    size?: 'sm' | 'md' | 'lg';
    showChannelIcon?: boolean;
    isSyncing?: boolean;
    inverted?: boolean;
    fallbackType?: 'initials' | 'icon';
    customBadgeIcon?: 'whatsapp' | 'instagram' | 'mic-received' | 'mic-sent';
    onClick?: () => void;
    borderColorClass?: string;
    badgeOnBrand?: boolean;
    /** Tenta carregar CDN WhatsApp (ex.: foto no player de áudio). Fallback no onError. */
    allowTemporaryUrl?: boolean;
}

export const ChatAvatar: React.FC<ChatAvatarProps> = ({
    name,
    avatar,
    size = 'md',
    showChannelIcon = true,
    onClick,
    isSyncing = false,
    inverted = false,
    fallbackType = 'icon',
    customBadgeIcon = 'whatsapp',
    badgeOnBrand = false,
    borderColorClass = 'border-border',
    allowTemporaryUrl = false,
}) => {
    // Só R2 permanente (pps/IG CDN = 403). allowTemporaryUrl: player de áudio com fallback onError.
    const shouldRenderImage = !!avatar
        && (allowTemporaryUrl || canRenderRemoteAvatarUrl(avatar));
    const [imgError, setImgError] = React.useState(() => isImageFailed(avatar) || !shouldRenderImage);

    React.useEffect(() => {
        setImgError(isImageFailed(avatar) || !shouldRenderImage);
    }, [avatar, shouldRenderImage]);

    const handleImgError = React.useCallback(() => {
        if (avatar) {
            markImageAsFailed(avatar);
            setImgError(true);
        }
    }, [avatar]);

    const sizeClasses = size === 'lg' ? 'w-11 h-11 text-sm' : size === 'md' ? 'w-9 h-9 text-xs' : 'w-7 h-7 text-[10px]';
    const iconSize = size === 'lg' ? 'w-[18px] h-[18px]' : size === 'md' ? 'w-4 h-4' : 'w-3 h-3';
    const badgeSize = size === 'lg' ? 'w-4 h-4' : size === 'md' ? 'w-3.5 h-3.5' : 'w-3 h-3';
    const badgeIconSize = size === 'lg' ? 'w-3 h-3' : size === 'md' ? 'w-2.5 h-2.5' : 'w-2 h-2';

    const getInitials = (nameStr?: string) => {
        if (!nameStr) return 'U';
        const parts = nameStr.trim().split(' ').filter(Boolean);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return nameStr.substring(0, 2).toUpperCase();
    };

    const getAvatarColorClass = (nameStr?: string) => {
        if (!nameStr) return 'bg-avatar-chart-1-bg text-avatar-chart-1-icon border-transparent';

        let hash = 0;
        for (let i = 0; i < nameStr.length; i++) {
            hash = nameStr.charCodeAt(i) + ((hash << 5) - hash);
        }
        const index = Math.abs(hash) % 5;

        const colors = [
            'bg-avatar-chart-1-bg text-avatar-chart-1-icon border-transparent',
            'bg-avatar-chart-2-bg text-avatar-chart-2-icon border-transparent',
            'bg-avatar-chart-3-bg text-avatar-chart-3-icon border-transparent',
            'bg-avatar-chart-4-bg text-avatar-chart-4-icon border-transparent',
            'bg-avatar-chart-5-bg text-avatar-chart-5-icon border-transparent',
        ];
        return colors[index];
    };

    const renderBadge = () => {
        if (!showChannelIcon) return null;

        const isMic = customBadgeIcon === 'mic-received' || customBadgeIcon === 'mic-sent';
        const isMicSent = customBadgeIcon === 'mic-sent';

        return (
            <div className={`chat-avatar-badge ${badgeSize} ${inverted || badgeOnBrand ? 'chat-avatar-badge--brand' : ''}`}>
                {isMic ? (
                    <AppIcon name="mic" className={`${iconSize} text-gray-400`} aria-hidden />
                ) : customBadgeIcon === 'instagram' ? (
                    <InstagramIcon className={badgeIconSize} aria-hidden />
                ) : (
                    <WhatsAppIcon className={`${iconSize} text-success-500`} aria-hidden />
                )}
            </div>
        );
    };

    if (shouldRenderImage && !imgError) {
        return (
            <div
                className={`relative ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
                onClick={onClick}
                title={onClick ? "Clique para atualizar a foto" : undefined}
            >
                {isSyncing && (
                    <div className="absolute inset-0 bg-surface/50 rounded-full flex items-center justify-center z-10 backdrop-blur-sm">
                        <div className="w-5 h-5 border-2 border-t-2 border-border border-t-accent rounded-full animate-spin" />
                    </div>
                )}
                <img
                    src={avatar}
                    alt={name}
                    className={`${sizeClasses} rounded-full object-cover border ${borderColorClass} relative z-0`}
                    loading="lazy"
                    decoding="async"
                    referrerPolicy="no-referrer"
                    onError={handleImgError}
                />
                {renderBadge()}
            </div>
        );
    }

    return (
        <div
            className={`relative ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
            onClick={onClick}
            title={onClick ? "Clique para atualizar a foto" : undefined}
        >
            <div className={`${sizeClasses} rounded-full ${inverted
                ? 'bg-white/20 text-white border-white/30 backdrop-blur-sm'
                : getAvatarColorClass(name)
                } flex items-center justify-center font-bold border ${borderColorClass}`}>
                {isSyncing ? (
                    <div className={`w-5 h-5 border-2 border-t-2 rounded-full animate-spin ${inverted ? 'border-white/20 border-t-white' : 'border-border border-t-accent'}`} />
                ) : fallbackType === 'initials' ? (
                    <span className="font-sans font-semibold tracking-tight">
                        {getInitials(name)}
                    </span>
                ) : (
                    <AppIcon name="user" fill="currentColor" className="w-[46%] h-[46%] opacity-80" />
                )}
            </div>
            {renderBadge()}
        </div>
    );
};
